/**
 * How far a client deploy has got, read from its log.
 *
 * A deploy prints about 25,000 lines. The portal asks CloudWatch for only the
 * few hundred that say something about progress (`PROGRESS_FILTER_PATTERN`) and
 * feeds them through `reduceProgress`. Pure functions, no AWS and no React, so
 * the same code reads a live run and a finished one.
 */
import { stripAnsi } from '../../../lib/deploy-failure';

/** The steps a deploy goes through, in order. `queued` and `starting` come from the run's record, the rest from its log. */
export type DeployPhase = 'queued' | 'starting' | 'synth' | 'init' | 'plan' | 'apply' | 'done' | 'failed';

/** The phases the log itself can show. */
export type LogPhase = 'starting' | 'synth' | 'init' | 'plan' | 'apply' | 'done';

export const PHASE_LABELS: Record<DeployPhase, string> = {
  queued: 'Queued',
  starting: 'Starting',
  synth: 'Synth',
  init: 'Init',
  plan: 'Plan',
  apply: 'Apply',
  done: 'Done',
  failed: 'Failed',
};

/** What each phase is doing, for the tooltip on the phase word. */
export const PHASE_HINTS: Record<DeployPhase, string> = {
  queued: 'Waiting for the deploy task to be created.',
  starting: 'The deploy task is starting. It pulls the image first, which takes about two minutes.',
  synth: 'Building the Terraform config for this client.',
  init: 'Terraform is starting up.',
  plan: 'Terraform is comparing the client with the image. This is the long quiet part.',
  apply: 'Terraform is changing resources.',
  done: 'The deploy finished.',
  failed: 'The deploy failed.',
};

/** The steps shown as a trail on a live run, in order. */
export const PHASE_TRAIL: DeployPhase[] = ['starting', 'synth', 'init', 'plan', 'apply'];

const LOG_PHASE_RANK: Record<LogPhase, number> = { starting: 0, synth: 1, init: 2, plan: 3, apply: 4, done: 5 };

const PROGRESS_TERMS = [
  'Creation complete after',
  'Modifications complete after',
  'Destruction complete after',
  'Plan:',
  'Apply complete',
  'Error:',
  'Synthesizing',
  'Initializing the backend',
  'Acquiring state lock',
  'No changes',
];

/**
 * The CloudWatch Logs filter that returns only progress lines. Measured on real
 * runs: 1,063 events for a 1,021-resource deploy, against about 25,000 for the
 * whole log.
 */
export const PROGRESS_FILTER_PATTERN = PROGRESS_TERMS.map((term) => `?"${term}"`).join(' ');

export interface PlanCounts {
  add: number;
  change: number;
  destroy: number;
}

export interface DeployProgress {
  /** The furthest phase the log has reached. */
  logPhase: LogPhase;
  /** When the log entered each phase (epoch ms, from the log line's own time). */
  phaseAt: Partial<Record<LogPhase, number>>;
  plan: PlanCounts | null;
  /** Resources the plan said it would touch. 0 until the plan line arrives. */
  total: number;
  /** Resources finished so far. */
  done: number;
  /** Terraform printed `Apply complete!`. */
  applyComplete: boolean;
  /** The plan found nothing to change. */
  noChanges: boolean;
  /** `Error:` lines seen so far. Terraform keeps going after one, so a run can be live with errors. */
  errors: number;
  lastError: string;
  /** The Terraform address of the resource that finished last. */
  lastResource: string;
  lastEventAt: number;
}

export function createProgress(): DeployProgress {
  return {
    logPhase: 'starting',
    phaseAt: {},
    plan: null,
    total: 0,
    done: 0,
    applyComplete: false,
    noChanges: false,
    errors: 0,
    lastError: '',
    lastResource: '',
    lastEventAt: 0,
  };
}

// The `numa-<client>` prefix is only on the first line of each chunk cdktf prints, so no pattern relies on it.
const RE_PLAN = /Plan: (\d+) to add, (\d+) to change, (\d+) to destroy/;
const RE_COMPLETE = /^\s*(?:numa-\S+\s+)?(\S+?)(?: \([^)]*\))?: (?:Creation|Modifications|Destruction) complete after /;
const RE_APPLY_COMPLETE = /Apply complete! Resources: (\d+) added, (\d+) changed, (\d+) destroyed/;
const RE_ERROR = /^\s*(?:numa-\S+\s+)?[│╷╵]?\s*Error:\s*(.+)$/;
// cdktf repeats the failure as "Error: non-zero exit code 1" after Terraform's own error. It is not a second error.
const RE_ERROR_ECHO = /non-zero exit code/i;
const RE_NO_CHANGES = /No changes\.|^\s*(?:numa-\S+\s+)?No changes\b/;
const RE_SYNTH = /Synthesizing/;
const RE_INIT = /Initializing the backend/;
const RE_LOCK = /Acquiring state lock/;

export interface ProgressEvent {
  message?: string;
  timestamp?: number;
}

/**
 * Fold new log lines into the progress so far. Returns the same object when no
 * line changed anything, so a caller can skip a re-render.
 *
 * A plan-only run ends at the plan line: there is no apply to wait for.
 */
export function reduceProgress(
  previous: DeployProgress,
  events: ProgressEvent[],
  mode: 'deploy' | 'plan' = 'deploy'
): DeployProgress {
  if (events.length === 0) return previous;
  const next: DeployProgress = { ...previous, phaseAt: { ...previous.phaseAt } };
  let changed = false;

  const enter = (phase: LogPhase, at: number) => {
    if (LOG_PHASE_RANK[phase] <= LOG_PHASE_RANK[next.logPhase]) return;
    next.logPhase = phase;
    next.phaseAt[phase] = at;
    changed = true;
  };

  for (const event of events) {
    const line = stripAnsi(event.message || '');
    const at = event.timestamp || 0;
    if (at > next.lastEventAt) {
      next.lastEventAt = at;
      changed = true;
    }

    const complete = RE_COMPLETE.exec(line);
    if (complete) {
      next.done += 1;
      next.lastResource = complete[1];
      enter('apply', at);
      changed = true;
      continue;
    }

    const plan = RE_PLAN.exec(line);
    if (plan) {
      next.plan = { add: Number(plan[1]), change: Number(plan[2]), destroy: Number(plan[3]) };
      next.total = next.plan.add + next.plan.change + next.plan.destroy;
      if (next.total === 0) {
        next.noChanges = true;
        enter('done', at);
      } else {
        enter(mode === 'plan' ? 'done' : 'apply', at);
      }
      changed = true;
      continue;
    }

    if (RE_APPLY_COMPLETE.test(line)) {
      next.applyComplete = true;
      enter('done', at);
      changed = true;
      continue;
    }

    const error = RE_ERROR.exec(line);
    if (error) {
      if (!RE_ERROR_ECHO.test(error[1])) {
        next.errors += 1;
        next.lastError = error[1].trim();
        changed = true;
      }
      continue;
    }

    if (RE_NO_CHANGES.test(line)) {
      next.noChanges = true;
      enter('done', at);
      changed = true;
      continue;
    }

    if (RE_LOCK.test(line)) enter('plan', at);
    else if (RE_INIT.test(line)) enter('init', at);
    else if (RE_SYNTH.test(line)) enter('synth', at);
  }

  return changed ? next : previous;
}

/**
 * 0 to 100, or null while there is nothing to measure yet (show a moving bar).
 *
 * The plan's total drifts by a few from what Terraform ends up doing, so the
 * count alone stops at 99 and only `Apply complete!` reaches 100.
 */
export function progressPercent(progress: DeployProgress | undefined): number | null {
  if (!progress) return null;
  if (progress.applyComplete || progress.noChanges) return 100;
  if (progress.logPhase === 'done') return 100;
  if (progress.total <= 0) return null;
  return Math.min(99, Math.floor((progress.done / progress.total) * 100));
}

/**
 * The phase to show. The record says whether a task exists and how the run
 * ended; the log says everything between.
 */
export function resolvePhase(
  progress: DeployProgress | undefined,
  run: { hasTask: boolean; outcome?: 'success' | 'failed' }
): DeployPhase {
  if (run.outcome === 'failed') return 'failed';
  if (run.outcome === 'success') return 'done';
  if (!run.hasTask) return 'queued';
  return progress ? progress.logPhase : 'starting';
}

/**
 * The filter for what a plan-only run found: Terraform's `Plan:` line, or its
 * "No changes. Your infrastructure matches the configuration." line.
 */
export const PLAN_RESULT_FILTER_PATTERN = '?"Plan:" ?"No changes"';

/** What a plan-only run found. `unknown`: the log holds neither line, or is gone. */
export type PlanOutcome = { kind: 'changes'; plan: PlanCounts } | { kind: 'no-changes' } | { kind: 'unknown' };

/**
 * What a plan-only run found, read from its log lines.
 *
 * The run's own status cannot say: the pipeline records every plan that ran
 * to the end as "no changes", whether or not it found any. So the answer
 * comes from what Terraform printed.
 */
export function planOutcome(events: ProgressEvent[]): PlanOutcome {
  const progress = reduceProgress(createProgress(), events, 'plan');
  if (progress.plan && progress.total > 0) return { kind: 'changes', plan: progress.plan };
  if (progress.noChanges) return { kind: 'no-changes' };
  return { kind: 'unknown' };
}

/** "12 to add, 3 to change, 0 to destroy", "No changes", or an empty string when it is not known. */
export function describePlanOutcome(outcome: PlanOutcome | undefined): string {
  if (!outcome || outcome.kind === 'unknown') return '';
  return outcome.kind === 'changes' ? formatPlan(outcome.plan) : 'No changes';
}

const NUMBER = new Intl.NumberFormat('en-NZ');

export function formatCount(value: number): string {
  return NUMBER.format(value);
}

/** "1,054 to add, 2,036 to change, 293 to destroy" */
export function formatPlan(plan: PlanCounts): string {
  return `${formatCount(plan.add)} to add, ${formatCount(plan.change)} to change, ${formatCount(plan.destroy)} to destroy`;
}

/**
 * A Terraform address cut down to what a person recognises.
 *
 * `aws_lambda_function.acme-company-profile_main-start_lambda_BC519A65` reads
 * `aws_lambda_function.company-profile_main-start`: the client's own name, the
 * generated hash and the word the resource type already says are noise.
 */
export function shortenResourceAddress(address: string, clientName?: string): string {
  const dot = address.indexOf('.');
  if (dot < 0) return address;
  const type = address.slice(0, dot);
  let name = address.slice(dot + 1).replace(/_[0-9A-F]{8}$/, '');
  if (clientName) {
    for (const prefix of [`${clientName}-`, `${clientName}_`, `numa-${clientName}-`, `numa_${clientName}_`]) {
      if (name.startsWith(prefix) && name.length > prefix.length) {
        name = name.slice(prefix.length);
        break;
      }
    }
  }
  if (type === 'aws_lambda_function') name = name.replace(/_lambda$/, '');
  if (name.length > 56) name = `${name.slice(0, 55)}…`;
  return `${type}.${name}`;
}
